/**
 * Button variant / color-token logic — framework-free (no Gatsby / theme-ui
 * imports) so it can be ported to `packages/ui` during the modernization
 * (DEV-744). Only the rendering in `button.js` stays theme-specific.
 *
 * Two families of variants:
 *  - legacy `solid` / `ghost` — reproduce today's rendering byte-for-byte so
 *    existing content is unchanged (DEV-744 acceptance).
 *  - new `primary` / `secondary` / `download` — vetted fg/bg pairs derived to
 *    stay ≥ 4.5:1 across every business-unit color (guardrail test).
 */

import {
  pickReadable,
  darken,
  contrastRatio,
  LIGHT_FOREGROUND,
} from "../utils/contrast"

/** Theme `text` color — readable (≥ 6:1) on white and on the download surface. */
export const NEUTRAL_TEXT = "#5a5d5f"
/** Distinct, CTA-independent surface for download buttons (theme secondaryBackground). */
export const DOWNLOAD_BG = "#efefef"

/** File extensions that should render as a download button. */
export const DOWNLOAD_EXTENSION_RE = /\.(pdf|docx?|xlsx?|pptx?|zip)(?:[?#]|$)/i
const LABELLED_EXTENSIONS = [
  "pdf",
  "doc",
  "docx",
  "xls",
  "xlsx",
  "ppt",
  "pptx",
  "zip",
]

const CANONICAL_VARIANTS = [
  "primary",
  "secondary",
  "download",
  "solid",
  "ghost",
]

/**
 * Normalize an incoming variant value to a canonical one. Unknown / empty
 * values fall back to `ghost` (the historical default → outline, unchanged).
 * @param {string} [variant]
 * @returns {"primary"|"secondary"|"download"|"solid"|"ghost"}
 */
export function resolveVariant(variant) {
  return CANONICAL_VARIANTS.includes(variant) ? variant : "ghost"
}

const STYLED_VARIANTS = ["primary", "secondary", "download"]

/**
 * True for the new variants. Components that carry their own legacy button
 * markup switch to the shared token styling only for these, so content without
 * a variant keeps rendering exactly as before.
 * @param {string} [variant]
 */
export function isStyledVariant(variant) {
  return STYLED_VARIANTS.includes(variant)
}

/**
 * True for legacy variants whose rendering must stay identical to today.
 * @param {string} [variant]
 */
export function isLegacyVariant(variant) {
  const v = resolveVariant(variant)
  return v === "solid" || v === "ghost"
}

/**
 * Resolve the fg/bg/border color tokens for a button.
 * @param {object} args
 * @param {string} [args.variant]
 * @param {string} args.businessUnitColor  the active mode's primary color (hex)
 * @param {"light"|"brand"|"image"} [args.surface]  what the button sits on:
 *   the white page (default), an area filled with the business-unit color, or
 *   a photo. Only the new variants adapt; legacy ones render as before.
 * @returns {{
 *   base: string, bg: string, color: string, borderColor: string,
 *   hoverBg: string, hoverColor: string, hoverBorderColor: string,
 *   focusRing: string,
 * }}
 */
export function getButtonTokens({
  variant,
  businessUnitColor,
  surface = "light",
}) {
  const bu = businessUnitColor || "#000000"
  const v = resolveVariant(variant)

  if (surface !== "light" && isStyledVariant(v)) {
    return getSurfaceTokens(v, bu, surface)
  }

  switch (v) {
    case "primary": {
      // Filled brand color with an automatically readable foreground.
      const hoverBg = darken(bu, 0.12)
      return {
        base: "styles.solidButton",
        bg: bu,
        color: pickReadable(bu),
        borderColor: bu,
        hoverBg,
        hoverColor: pickReadable(hoverBg),
        hoverBorderColor: hoverBg,
        focusRing: bu,
      }
    }
    case "secondary": {
      // Outline: neutral text on white (page background), brand-colored border.
      return {
        base: "styles.ghostButton",
        bg: "transparent",
        color: NEUTRAL_TEXT,
        borderColor: bu,
        hoverBg: bu,
        hoverColor: pickReadable(bu),
        hoverBorderColor: bu,
        focusRing: bu,
      }
    }
    case "download": {
      // Distinct neutral surface so downloads read apart from CTAs.
      const hoverBg = darken(DOWNLOAD_BG, 0.08)
      return {
        base: "styles.solidButton",
        bg: DOWNLOAD_BG,
        color: NEUTRAL_TEXT,
        borderColor: DOWNLOAD_BG,
        hoverBg,
        hoverColor: NEUTRAL_TEXT,
        hoverBorderColor: hoverBg,
        focusRing: bu,
      }
    }
    case "solid": {
      // Legacy — unchanged rendering.
      return {
        base: "styles.solidButton",
        bg: bu,
        color: "#fff",
        borderColor: bu,
        hoverBg: bu,
        hoverColor: "#fff",
        hoverBorderColor: bu,
        focusRing: bu,
      }
    }
    case "ghost":
    default: {
      // Legacy — unchanged rendering.
      return {
        base: "styles.ghostButton",
        bg: "transparent",
        color: bu,
        borderColor: bu,
        hoverBg: bu,
        hoverColor: "#fff",
        hoverBorderColor: bu,
        focusRing: bu,
      }
    }
  }
}

/**
 * Darken `color` until it reaches AA as text on `background`.
 * @param {string} color
 * @param {string} background
 */
function readableOn(color, background) {
  let result = color
  for (let step = 1; contrastRatio(result, background) < 4.5; step++) {
    result = darken(color, step * 0.05)
  }
  return result
}

/**
 * Tokens for buttons on a brand-colored area or a photo. The pairs mirror the
 * light-surface ones: primary becomes a readable fill with brand-colored text,
 * secondary an outline in the readable foreground. The download chip is filled
 * and neutral, so it only needs a focus ring that shows on the surface.
 */
function getSurfaceTokens(variant, bu, surface) {
  // On a brand area the readable foreground is known (≥ 4.58:1 by pickReadable).
  // On a photo it is not — keep the historical white, as the legacy buttons do.
  const onSurface = surface === "brand" ? pickReadable(bu) : LIGHT_FOREGROUND
  const onFill = surface === "brand" ? bu : readableOn(bu, LIGHT_FOREGROUND)

  switch (variant) {
    case "primary":
      return {
        base: "styles.solidButton",
        bg: onSurface,
        color: onFill,
        borderColor: onSurface,
        hoverBg: "transparent",
        hoverColor: onSurface,
        hoverBorderColor: onSurface,
        focusRing: onSurface,
      }
    case "secondary":
      return {
        base: "styles.ghostButton",
        bg: "transparent",
        color: onSurface,
        borderColor: onSurface,
        hoverBg: onSurface,
        hoverColor: onFill,
        hoverBorderColor: onSurface,
        focusRing: onSurface,
      }
    case "download":
    default:
      return {
        ...getButtonTokens({ variant: "download", businessUnitColor: bu }),
        focusRing: onSurface,
      }
  }
}

/**
 * theme-ui style object for a token set — colors, hover and focus. Sizing and
 * layout stay with the component that renders the button.
 * @param {ReturnType<typeof getButtonTokens>} tokens
 */
export function buttonColorSx(tokens) {
  return {
    variant: tokens.base,
    bg: tokens.bg,
    color: tokens.color,
    borderColor: tokens.borderColor,
    "&:hover": {
      bg: tokens.hoverBg,
      color: tokens.hoverColor,
      borderColor: tokens.hoverBorderColor,
    },
    "&:focus-visible": {
      outline: `2px solid ${tokens.focusRing}`,
      outlineOffset: "2px",
    },
  }
}

/**
 * What a link button shows, from the editor's settings. Shared by every blok
 * with a link button (DEV-744/745), so they all auto-detect downloads the same
 * way: an asset link or a document/archive URL — or an explicit
 * `variant: download` — renders as a download with the file-type icon left and
 * the download arrow right. The file type is never added to the label.
 * @param {object} args
 * @param {string | object} [args.link]  the raw link (multilink object or URL);
 *   defaults to `href`
 * @param {string} [args.href]  the resolved href the button links to
 * @param {string} [args.variant]  the editor's variant; unset stays unset
 * @param {boolean} [args.iconRight]  the editor's "arrow right" checkbox
 * @param {boolean} [args.proxy]  rewrite Storyblok assets onto the same-origin
 *   download proxy (production builds only, see ASSET_PROXY_PREFIX)
 * @returns {{ href?: string, download: boolean, variant?: string,
 *   iconLeft?: string, iconRight?: string }}
 */
export function resolveButtonDisplay({
  link,
  href,
  variant,
  iconRight,
  proxy = false,
}) {
  const download = isDownloadLink(link || href) || variant === "download"
  if (!download) {
    return {
      href,
      download: false,
      variant: variant || undefined,
      iconLeft: undefined,
      iconRight: iconRight ? "right" : undefined,
    }
  }
  return {
    href: proxy ? toProxiedAssetUrl(href) : href,
    download: true,
    variant: "download",
    iconLeft: getFileIcon(getFileType(href)),
    iconRight: "download",
  }
}

/**
 * Whether a link should render as a download. Accepts either a plain URL string
 * (the `button` blok uses a Text link field) or a Storyblok multilink object
 * (other bloks). Detects an uploaded asset (`linktype: "asset"`) or a URL whose
 * extension is a known document/archive type.
 * @param {string | { linktype?: string, url?: string }} link
 * @returns {boolean}
 */
export function isDownloadLink(link) {
  if (!link) return false
  if (typeof link === "string") return DOWNLOAD_EXTENSION_RE.test(link)
  // An asset multilink keeps its linktype after the file is removed.
  if (link.linktype === "asset") return !!link.url
  return typeof link.url === "string" && DOWNLOAD_EXTENSION_RE.test(link.url)
}

const FILE_ICONS = {
  PDF: "fileText",
  DOC: "fileText",
  DOCX: "fileText",
  XLS: "fileSpreadsheet",
  XLSX: "fileSpreadsheet",
  PPT: "fileChart",
  PPTX: "fileChart",
  ZIP: "fileArchive",
}

/**
 * Icon name for the left slot of a download button, derived from the file
 * type returned by getFileType (e.g. "PDF"). Unknown or missing types get
 * the generic file icon, so an explicit `variant: download` still shows one.
 * @param {string|null} [fileType]
 * @returns {"file"|"fileText"|"fileSpreadsheet"|"fileChart"|"fileArchive"}
 */
export function getFileIcon(fileType) {
  return (fileType && FILE_ICONS[String(fileType).toUpperCase()]) || "file"
}

/**
 * Same-origin path that Netlify proxies to `https://a.storyblok.com/f/*`
 * (rule in every site's `static/_redirects`). Browsers ignore the `download`
 * attribute on cross-origin links and the Storyblok asset CDN sends no
 * Content-Disposition, so downloads have to be served from our own origin.
 */
export const ASSET_PROXY_PREFIX = "/assets/dl"
const STORYBLOK_ASSET_RE = /^(?:https?:)?\/\/a\.storyblok\.com\/f\/(.+)$/i

/**
 * Rewrite a Storyblok asset URL onto the same-origin download proxy path.
 * Anything that is not an `a.storyblok.com/f/…` URL is returned unchanged.
 * @param {string} [url]
 * @returns {string|undefined}
 */
export function toProxiedAssetUrl(url) {
  if (typeof url !== "string") return url
  const match = url.match(STORYBLOK_ASSET_RE)
  return match ? `${ASSET_PROXY_PREFIX}/${match[1]}` : url
}

/**
 * File type of a URL, e.g. "PDF" for `…/x.pdf` — picks the download icon.
 * Returns null for URLs without a recognized document/archive extension.
 * @param {string} [url]
 * @returns {string|null}
 */
export function getFileType(url) {
  if (typeof url !== "string") return null
  const match = url.match(/\.([a-z0-9]+)(?:[?#]|$)/i)
  if (!match) return null
  const ext = match[1].toLowerCase()
  return LABELLED_EXTENSIONS.includes(ext) ? ext.toUpperCase() : null
}
